import React, { useState } from 'react';
import { Container } from 'react-bootstrap';
import { FaPhoneAlt } from 'react-icons/fa';
import Icon from '../../../components/ui/Icon';
import Button from '../../../components/ui/Button';
import { ScrollText, Reveal } from '../../../components/motion';
import { BRANCHES, MAIN_PHONE, cityLabel, telHref } from '../../../constants/contact';
import BranchesMap, { HQ_CITY } from './BranchesMap';
import Eyebrow from '../../../components/ui/Eyebrow';
import styles from './BranchesSection.module.css';

/** On phones only the first few branches show until "Show all" is tapped. */
const PHONE_PREVIEW = 6;

// Head office first, then the rest in their usual order.
const LIST = [...BRANCHES.filter((b) => b.city === HQ_CITY), ...BRANCHES.filter((b) => b.city !== HQ_CITY)];

/**
 * Our Branches — a Sahari-red dot map of Saudi Arabia with every branch
 * pinned and linked to the Riyadh head office, beside the list of branches
 * to call. Pointing at a branch lights up its pin.
 */
const BranchesSection: React.FC = () => {
  const [showAll, setShowAll] = useState(false);
  const [active, setActive] = useState<string | null>(null);

  return (
    <section className={styles.branches}>
      <Container fluid="xl" className={styles.inner}>
        <div className={styles.layout}>
          <Reveal className={styles.mapCard}>
            <div className={styles.legend} aria-hidden="true">
              <span>
                <i className={styles.legendHq} />
                Head office
              </span>
              <span>
                <i className={styles.legendBranch} />
                Branch
              </span>
            </div>
            <BranchesMap cities={LIST.map((b) => b.city)} active={active} label={cityLabel} />
            <div className={styles.countBadge}>
              <b>{BRANCHES.length}</b>
              <span>
                branches across
                <br />
                Saudi Arabia
              </span>
            </div>
          </Reveal>

          <div className={styles.side}>
            <Eyebrow>Our branches</Eyebrow>
            <ScrollText as="h2" className={styles.title}>
              Walk in, call, or we come to you
            </ScrollText>
            <Reveal as="p" className={styles.subtitle} delay={0.05}>
              Twelve Sahari branches from Duba to Jizan. Drop your boxes at the nearest one — or call and our
              driver picks them up.
            </Reveal>

            <ul className={`${styles.list} ${showAll ? '' : styles.collapsed}`} onMouseLeave={() => setActive(null)}>
              {LIST.map((branch, i) => (
                <li
                  key={branch.city}
                  className={`${styles.row} ${branch.city === active ? styles.rowOn : ''} ${i >= PHONE_PREVIEW ? styles.extra : ''}`}
                  onMouseEnter={() => setActive(branch.city)}
                >
                  <div className={styles.rowText}>
                    <h3 className={styles.city}>
                      {cityLabel(branch.city)}
                      {branch.city === HQ_CITY && <em className={styles.hq}>HQ</em>}
                    </h3>
                    <span className={styles.tel}>{branch.phone}</span>
                  </div>
                  <a
                    className={styles.callBtn}
                    href={telHref(branch.phone)}
                    aria-label={`Call ${cityLabel(branch.city)} branch, ${branch.phone}`}
                    onFocus={() => setActive(branch.city)}
                  >
                    <Icon icon={FaPhoneAlt} />
                  </a>
                </li>
              ))}
            </ul>

            {!showAll && (
              <button type="button" className={styles.showAll} onClick={() => setShowAll(true)}>
                Show all {BRANCHES.length} branches
              </button>
            )}

            <div className={styles.actions}>
              <Button to="/book-shipment" variant="primary">
                Book a pickup
              </Button>
              <a className={styles.hqLink} href={telHref(MAIN_PHONE)}>
                Head office · {MAIN_PHONE}
              </a>
            </div>
          </div>
        </div>
      </Container>
    </section>
  );
};

export default BranchesSection;
